import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Map as CarteGL, MapMouseEvent } from 'maplibre-gl';
import { Carte, type SelectionCarte } from './carte';
import { useTrace, correctionDirecte } from './trace';
import { BarreTrace } from './barre-trace';
import { ReperesCarte } from './reperes-carte';
import { valeursSelectionBarre, valeursTraceBarre } from './barre-etat-valeurs';
import { api, ErreurApi } from './api';
import { paletteCarte } from './palette-carte.js';
import {
  depuisGeoJSON, versGeoJSON, type Figure, type Point, type PolygoneGeoJSON,
} from '../../src/partage/edition-geometrie.js';
import {
  annulerLot, ecartAuContact, executerLot, ouvrirChantier, peutAnnulerLot, peutRefaireLot,
  preparerDeplacementPartage, refaireLot, sommetsCoincidents,
  type Chantier, type DeplacementPartage, type Designation,
} from '../../src/partage/topologie-partagee.js';
import { avancerAuGisement, type UniteAngle } from '../../src/partage/cogo-gisement.js';
import type { DemandeImmatriculation } from './api';
import { TOUCHES_ATELIER } from './touches-atelier';

/**
 * L'ATELIER SIG — la PAGE du métier d'édition. Items D.968, D.946, D.998.
 *
 * ⚠️⚠️ « LE MODE ÉDITION N'EST PAS OUVERT À TOUT LE MONDE : CEUX QUI FONT DE
 * L'ÉDITION ONT LEUR PROPRE PAGE » — le fondateur, réaffirmé le 11/10/2026.
 * La barre « Emprise · Bornes · Voie » flottait sur l'écran de CONSULTATION,
 * conditionnée à une permission (D.998) : le garde était juste, la place ne
 * l'était pas. Ici, l'édition est une RUBRIQUE, servie aux seuls porteurs de
 * `cad.parcelle : creer | modifier` — les deux permissions que les routes du
 * service exigent elles-mêmes. L'écran carte, lui, ne garde que la MESURE.
 *
 * ⚠️⚠️ LE CHANTIER MITOYEN EST LA MOITIÉ QUI MANQUAIT — D.968. Le moteur
 * (`src/partage/topologie-partagee.ts`) et la route de lot
 * (`PATCH /parcelles/emprises`, transaction solidaire) existaient, éprouvés
 * par deux bancs, et AUCUN écran ne les appelait : déplacer une borne
 * mitoyenne corrigeait UN fonds et ouvrait un interstice avec l'autre — le
 * trou que personne ne voit avant le litige de bornage.
 *
 * ⚠️ UNE SEULE CARTE, LE MÊME COMPOSANT. Cette page monte `<Carte>` — le même
 * moteur que la consultation, jamais une seconde implémentation (la leçon
 * `carte.tsx`/`assemblage.tsx` est écrite en tête des deux fichiers).
 *
 * ⚠️ LE DÉPÔT NE BOUCLE JAMAIS SUR LA ROUTE UNITAIRE. Deux `PATCH` unitaires
 * successifs ouvrent l'interstice ENTRE les appels — définitif si le second
 * échoue. ≥ 2 corrections → la route de LOT (tout ou rien) ; exactement 1 —
 * un déplacement qui n'a finalement touché qu'un fonds — → l'unitaire.
 */

const SOURCE_CHANTIER = 'atelier-chantier';
const CALQUES_CHANTIER = ['chantier-contours', 'chantier-sommets', 'chantier-designe', 'chantier-cible'];

/** Clé stable d'une désignation — pour les Set et les properties de tuile. */
const cleDe = (d: Designation) => `${d.figureId}|${d.anneauId}|${d.sommetId}`;

interface FigureChargee {
  identifiant: string;
  statut: string;
  figure: Figure;
}

interface EtatChantier {
  ch: Chantier;
  /** Ce que chaque figure EST au registre — affiché, jamais deviné. */
  fiches: Map<string, { statut: string }>;
  /** Les parcelles trouvées dans l'emprise et NON chargées, avec leur raison. */
  ecartees: Array<{ identifiant: string; raison: string }>;
  voisinesProjetEcartees: number;
}

export function AtelierSig(
  { reglages, parcelleInitiale = null, onParcelleConsommee, onOuvrirParcelle,
    peutAppliquer, peutProposer }:
  {
    reglages?: {
      zoomMinimal?: { divisions: number; sections?: number; parcelles: number; batiments: number };
      zoomNomsDeRue?: number;
      zoomBatiComplet?: number;
      vueConseillee?: {
        centre: [number, number]; zoom: number;
        division: string | null; parcelles: number; motif: string;
      } | null;
      couverture?: { parcellesAuPlan: number; parcellesOpposables: number; parcellesActives: number; divisionsPeuplees: number };
    } | null;
    /** La parcelle qui a voyagé avec la rubrique — consommée UNE fois. */
    parcelleInitiale?: string | null;
    onParcelleConsommee?: () => void;
    onOuvrirParcelle?: (id: string) => void;
    /**
     * ⚠️ LA FOURCHE PAR MÉTIER (D.1488) — mesurée le 11/10/2026 : le rôle
     * geometre porte cad.parcelle:creer SEUL, et le dépôt direct exige
     * `modifier` — sans cette fourche, le géomètre voyait l'atelier, traçait,
     * et son dépôt rendait 403. Le porteur de `modifier` APPLIQUE au registre ;
     * le porteur de cad.releve:creer DÉPOSE UN PROJET DE LEVÉ qu'un tiers
     * décide. L'écran NOMME le geste avant le clic — jamais après l'échec.
     */
    peutAppliquer: boolean;
    peutProposer: boolean;
  },
) {
  const [carteGL, setCarteGL] = useState<CarteGL | null>(null);
  const [gabaritTuiles, setGabaritTuiles] = useState<string | undefined>(undefined);
  const relireVersionDuPlan = useCallback(async () => {
    try { setGabaritTuiles((await api.versionDuPlan()).gabarit); }
    catch { /* sans gabarit, la carte retombe sur le chemin non versionné */ }
  }, []);
  useEffect(() => { void relireVersionDuPlan(); }, [relireVersionDuPlan]);

  /* Le projet de levé porte la DÉCLARATION du géomètre : méthode et précision
     annoncée. Modifiables — les pré-remplir n'invente rien, les figer mentirait. */
  const [methodeLeve, setMethodeLeve] = useState('gnss_rtk');
  const [precisionLeve, setPrecisionLeve] = useState(0.1);
  const refLeve = useRef({ methode: methodeLeve, precision: precisionLeve });
  useEffect(() => { refLeve.current = { methode: methodeLeve, precision: precisionLeve }; },
    [methodeLeve, precisionLeve]);

  /* Le STATUT de la parcelle reprise, lu avec sa fiche : c'est lui qui
     aiguille le geste de dépôt — et le bouton le NOMME avant le clic. */
  const refStatuts = useRef(new Map<string, string>());
  const [gesteReprise, setGesteReprise] = useState<'direct' | 'acte' | 'projet'>('direct');

  /* L'écrivain de la reprise mono-figure, choisi par le MÉTIER puis par le
     STATUT (D.1488 + D.1499) : l'appliqueur écrit DIRECTEMENT un projet de
     parcelle, dépose une MUTATION DE RECTIFICATION pour un fonds inscrit ;
     le géomètre propose un projet de levé. Jamais un 409 à lire après coup. */
  const proposerLeve = useCallback(async (identifiant: string, corps: {
    geometrie: unknown; motif: string;
  }) => {
    const r = await api.releves.deposer({
      identifiantParcelle: identifiant,
      methode: refLeve.current.methode,
      precisionAnnonceeM: refLeve.current.precision,
      geometrie: corps.geometrie,
      observations: corps.motif,
    });
    return { identifiant: r.releve.reference, statut: r.releve.statut,
             acte: 'proposition' as const, ecartSurfacePct: null };
  }, []);
  const ecrireCorrection = useCallback(async (identifiant: string, corps: {
    geometrie: unknown; origine: DemandeImmatriculation['origine'];
    precisionM?: number; methode?: string; source?: string; motif: string;
  }) => {
    if (!peutAppliquer) return proposerLeve(identifiant, corps);
    if (refStatuts.current.get(identifiant) === 'projet') {
      return correctionDirecte(identifiant, corps);
    }
    /* Un fonds inscrit ne se corrige pas d'un clic : l'acte le corrige. Le
       serveur disait déjà ce refus — l'écran le devance au lieu de le subir. */
    const r = await api.mutations.deposer({
      type: 'rectification', identifiantParcelle: identifiant,
      effetProjete: {
        geometrie: corps.geometrie, origine: corps.origine,
        ...(corps.precisionM !== undefined ? { precisionM: corps.precisionM } : {}),
        ...(corps.methode ? { methode: corps.methode } : {}),
        ...(corps.source ? { source: corps.source } : {}),
      },
      motif: corps.motif,
    });
    return { identifiant: r.mutation.reference, statut: r.mutation.statut,
             acte: 'acte_depose' as const, ecartSurfacePct: null };
  }, [peutAppliquer, proposerLeve]);

  const [etatTrace, gestesTrace] = useTrace(carteGL, relireVersionDuPlan, ecrireCorrection);

  const [erreur, setErreur] = useState<string | null>(null);
  const [parcelleDesignee, setParcelleDesignee] = useState<string | null>(null);
  const [selectionCourante, setSelectionCourante] = useState<SelectionCarte | null>(null);
  const recevoirSelection = useCallback((sel: SelectionCarte | null) => {
    setParcelleDesignee(sel?.couche === 'parcelles' ? sel.identifiant : null);
    setSelectionCourante(sel);
  }, []);

  /** Reprendre une emprise pour la corriger — le chemin mono-figure. */
  const modifierEmprise = useCallback(async (identifiant?: string) => {
    const cible = identifiant ?? parcelleDesignee;
    if (!cible) return;
    try {
      const fiche = await api.parcelles.fiche(cible);
      const g = (fiche as any)?.geometrie ?? (fiche as any)?.parcelle?.geometrie;
      if (!g) { setErreur(`La parcelle ${cible} ne rend aucune géométrie.`); return; }
      const statut = String((fiche as any)?.parcelle?.statut ?? (fiche as any)?.statut ?? '');
      refStatuts.current.set(cible, statut);
      setGesteReprise(!peutAppliquer ? 'projet' : statut === 'projet' ? 'direct' : 'acte');
      gestesTrace.reprendre(g, cible);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : `Parcelle ${cible} illisible.`);
    }
  }, [parcelleDesignee, gestesTrace, peutAppliquer]);

  /**
   * LA PARCELLE QUI VOYAGE AVEC LA RUBRIQUE — consommée une fois, comme le
   * Guichet le fait (`parcellePourDemande`, app.tsx). Elle est DÉSIGNÉE, pas
   * ouverte en modification d'office : l'agent choisit mono-figure ou
   * chantier mitoyen — deux gestes différents sur la même parcelle.
   */
  const initiale = useRef(false);
  useEffect(() => {
    if (initiale.current || !parcelleInitiale) return;
    initiale.current = true;
    setParcelleDesignee(parcelleInitiale);
    onParcelleConsommee?.();
  }, [parcelleInitiale, onParcelleConsommee]);

  /* ════════════════════════════════════════════════════════════════════════
     LE CHANTIER MITOYEN — plusieurs fonds, un seul geste.
     ════════════════════════════════════════════════════════════════════════ */
  const [chantier, setChantier] = useState<EtatChantier | null>(null);
  const [designe, setDesigne] = useState<Designation | null>(null);
  const [coincidents, setCoincidents] = useState<Designation[]>([]);
  const [preparation, setPreparation] = useState<DeplacementPartage | null>(null);
  const [cible, setCible] = useState<Point | null>(null);
  const [cibleAccrochee, setCibleAccrochee] = useState<string | null>(null);
  const [lotsAppliques, setLotsAppliques] = useState(0);
  /**
   * ⚠️ LA TOLÉRANCE EST EN MÈTRES, AFFICHÉE ET RÉGLABLE. Deux bornes « au
   * même endroit » relevées à dix ans d'écart diffèrent de quelques
   * centimètres ; une égalité stricte ne trouverait JAMAIS de sommet partagé
   * et le chantier serait inerte en paraissant marcher.
   */
  const [toleranceM, setToleranceM] = useState(0.10);
  const [chargement, setChargement] = useState(false);
  const [depotEnCours, setDepotEnCours] = useState(false);
  const [resultatDepot, setResultatDepot] = useState<{
    /** Référence du PROJET déposé (géomètre) — exclusif des champs d'application. */
    projet?: string;
    corrigees: number; ecart: number | null; chevauchements: number;
  } | null>(null);
  const [motif, setMotif] = useState('');
  const [origine, setOrigine] = useState<DemandeImmatriculation['origine']>('leve_terrain');
  const [uniteCogo, setUniteCogo] = useState<UniteAngle>('grade');
  const [cogoGisement, setCogoGisement] = useState('');
  const [cogoDistance, setCogoDistance] = useState('');

  /* Des refs-miroirs pour l'écouteur de clic, posé UNE fois par chantier. */
  const refChantier = useRef(chantier); refChantier.current = chantier;
  const refDesigne = useRef(designe); refDesigne.current = designe;
  const refTolerance = useRef(toleranceM); refTolerance.current = toleranceM;
  const refPreparation = useRef(preparation); refPreparation.current = preparation;

  /** Les identifiants réellement MODIFIÉS par les lots appliqués. */
  const figuresModifiees = useMemo(() => {
    if (!chantier) return [];
    const ids = new Set<string>();
    for (const lot of chantier.ch.passe) for (const g of lot.gestes) ids.add(g.figureId);
    return [...ids];
  }, [chantier, lotsAppliques]);   // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * OUVRIR LE CHANTIER — la parcelle désignée + ses voisines, depuis la BASE.
   *
   * ⚠️ JAMAIS DEPUIS LES TUILES : une tuile coupe les géométries à sa
   * frontière, et l'on s'accrocherait sur un point de couture (règle écrite
   * dans `trace.tsx`, tenue ici pour la même raison).
   *
   * ⚠️ LES MAILLES « projet » SONT ÉCARTÉES À LA LECTURE, ET C'EST DIT. Elles
   * sont 99,9996 % du plan : les charger noierait les vraies voisines sous
   * le carroyage, et le service refuse de les corriger de toute façon.
   *
   * ⚠️ LE MULTI-CORPS EST ÉCARTÉ AVANT D'OUVRIR, PAS AU DÉPÔT. L'éditeur ne
   * tient qu'un corps : réécrire `geom` depuis le premier corps EFFACERAIT
   * les autres. Le service le refuse — mais le refus arriverait après le
   * travail de l'agent. Un atelier qui ne sait pas résoudre un cas le NOMME
   * à l'entrée.
   */
  const ouvrirChantierMitoyen = useCallback(async () => {
    if (!parcelleDesignee) return;
    setChargement(true); setErreur(null); setResultatDepot(null);
    try {
      const fiche = await api.parcelles.fiche(parcelleDesignee);
      const g = (fiche as any)?.geometrie ?? (fiche as any)?.parcelle?.geometrie;
      if (!g) { setErreur(`${parcelleDesignee} ne rend aucune géométrie.`); return; }

      /* L'emprise de recherche : la boîte de la parcelle, élargie de ~30 m. */
      let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
      const parcourir = (n: unknown): void => {
        if (!Array.isArray(n)) return;
        if (typeof n[0] === 'number' && typeof n[1] === 'number') {
          const [x, y] = n as [number, number];
          if (x < minLon) minLon = x; if (x > maxLon) maxLon = x;
          if (y < minLat) minLat = y; if (y > maxLat) maxLat = y;
          return;
        }
        for (const e of n) parcourir(e);
      };
      parcourir((g as { coordinates: unknown }).coordinates);
      const marge = 30 / 111_320;   // ~30 m en degrés de latitude
      const col = await api.parcelles.dansEnveloppe(
        { ouest: minLon - marge, sud: minLat - marge, est: maxLon + marge, nord: maxLat + marge }, 400);

      const chargees: FigureChargee[] = [];
      const ecartees: Array<{ identifiant: string; raison: string }> = [];
      let projetEcartees = 0;
      for (const f of (col as any)?.features ?? []) {
        const id = String(f?.properties?.identifiant ?? f?.id ?? '');
        const statut = String(f?.properties?.statut ?? '');
        const geo = f?.geometry;
        if (!id || !geo) continue;
        if (statut === 'projet' && id !== parcelleDesignee) { projetEcartees += 1; continue; }
        const polys: number[][][][] = geo.type === 'MultiPolygon' ? geo.coordinates
          : geo.type === 'Polygon' ? [geo.coordinates] : [];
        if (polys.length === 0) { ecartees.push({ identifiant: id, raison: 'géométrie non surfacique' }); continue; }
        if (polys.length > 1) {
          ecartees.push({ identifiant: id, raison: `${polys.length} corps — l'éditeur n'en tient qu'un, corriger écraserait les autres` });
          continue;
        }
        chargees.push({
          identifiant: id, statut,
          figure: depuisGeoJSON({ type: 'Polygon', coordinates: polys[0]! } as PolygoneGeoJSON),
        });
      }
      if (!chargees.some((x) => x.identifiant === parcelleDesignee)) {
        /* La désignée elle-même peut être multi-corps ou hors enveloppe servie. */
        const raison = ecartees.find((e) => e.identifiant === parcelleDesignee)?.raison
          ?? 'absente de la réponse du service';
        setErreur(`Le chantier ne peut pas s'ouvrir sur ${parcelleDesignee} : ${raison}.`);
        return;
      }
      const ch = ouvrirChantier(Object.fromEntries(chargees.map((x) => [x.identifiant, x.figure])));
      setChantier({
        ch,
        fiches: new Map(chargees.map((x) => [x.identifiant, { statut: x.statut }])),
        ecartees,
        voisinesProjetEcartees: projetEcartees,
      });
      setDesigne(null); setCoincidents([]); setPreparation(null); setCible(null);
      setCibleAccrochee(null); setLotsAppliques(0); setMotif('');
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String(e));
    } finally {
      setChargement(false);
    }
  }, [parcelleDesignee]);

  const fermerChantier = useCallback(() => {
    setChantier(null); setDesigne(null); setCoincidents([]); setPreparation(null);
    setCible(null); setCibleAccrochee(null); setResultatDepot(null);
  }, []);

  /* ── Peindre le chantier sur la carte ──────────────────────────────────── */
  const peindreChantier = useCallback(() => {
    const c = carteGL;
    if (!c || !c.getSource(SOURCE_CHANTIER)) return;
    const et = refChantier.current;
    const features: unknown[] = [];
    if (et) {
      for (const [figId, f] of et.ch.figures) {
        const gj = versGeoJSON(f);
        features.push({
          type: 'Feature',
          properties: { r: 'contour', fig: figId, modifie: figuresModifiees.includes(figId) },
          geometry: gj,
        });
        for (const a of f.anneaux) {
          for (const s of a.sommets) {
            const d: Designation = { figureId: figId, anneauId: a.id, sommetId: s.id };
            features.push({
              type: 'Feature',
              properties: {
                r: refDesigne.current && cleDe(refDesigne.current) === cleDe(d) ? 'designe' : 'sommet',
                fig: figId, anneau: a.id, sommet: s.id,
              },
              geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
            });
          }
        }
      }
    }
    if (cible) {
      features.push({
        type: 'Feature', properties: { r: 'cible' },
        geometry: { type: 'Point', coordinates: [cible.lon, cible.lat] },
      });
    }
    (c.getSource(SOURCE_CHANTIER) as any).setData({ type: 'FeatureCollection', features });
  }, [carteGL, cible, figuresModifiees]);

  useEffect(() => { peindreChantier(); }, [peindreChantier, chantier, designe, lotsAppliques]);

  /* La source et ses calques — montés tant que la page vit. */
  useEffect(() => {
    const c = carteGL;
    if (!c) return;
    let vivant = true;
    const monter = () => {
      if (!vivant || c.getSource(SOURCE_CHANTIER)) return;
      const P = paletteCarte();
      c.addSource(SOURCE_CHANTIER, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      c.addLayer({
        id: 'chantier-contours', type: 'line', source: SOURCE_CHANTIER,
        filter: ['==', ['get', 'r'], 'contour'],
        paint: {
          'line-color': ['case', ['get', 'modifie'], P.choix, P.section],
          'line-width': ['case', ['get', 'modifie'], 3, 2],
        },
      });
      c.addLayer({
        id: 'chantier-sommets', type: 'circle', source: SOURCE_CHANTIER,
        filter: ['==', ['get', 'r'], 'sommet'],
        paint: {
          'circle-radius': 4.5, 'circle-color': P.fond,
          'circle-stroke-color': P.section, 'circle-stroke-width': 1.5,
        },
      });
      /* ⚠️ LE TÉMOIN VISUEL — D.946 : le sommet désigné et la cible sont
         PEINTS, pas seulement nommés dans un panneau. */
      c.addLayer({
        id: 'chantier-designe', type: 'circle', source: SOURCE_CHANTIER,
        filter: ['==', ['get', 'r'], 'designe'],
        paint: {
          'circle-radius': 7, 'circle-color': P.choix,
          'circle-stroke-color': P.fond, 'circle-stroke-width': 2,
        },
      });
      c.addLayer({
        id: 'chantier-cible', type: 'circle', source: SOURCE_CHANTIER,
        filter: ['==', ['get', 'r'], 'cible'],
        paint: {
          'circle-radius': 6, 'circle-color': 'transparent',
          'circle-stroke-color': P.choix, 'circle-stroke-width': 2.5,
        },
      });
      peindreChantier();
    };
    if (c.isStyleLoaded()) monter(); else c.once('load', monter);
    return () => {
      vivant = false;
      for (const id of CALQUES_CHANTIER) if (c.getLayer(id)) c.removeLayer(id);
      if (c.getSource(SOURCE_CHANTIER)) c.removeSource(SOURCE_CHANTIER);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carteGL]);

  /** Préparer un déplacement vers `vers` — avec accrochage de la CIBLE. */
  const preparerVers = useCallback((vers: Point) => {
    const et = refChantier.current, d = refDesigne.current;
    if (!et || !d) return;
    /* ⚠️ LA CIBLE S'ACCROCHE AUSSI : viser un coin existant doit écrire LE
       point de ce coin, pas un point à deux centimètres — sinon chaque geste
       fabrique une quasi-coïncidence que la tolérance devra rattraper. */
    const proches = sommetsCoincidents(et.ch, vers, refTolerance.current)
      .filter((x) => cleDe(x) !== cleDe(d));
    let point = vers; let accroche: string | null = null;
    if (proches[0]) {
      const f = et.ch.figures.get(proches[0].figureId)!;
      const s = f.anneaux.find((a) => a.id === proches[0]!.anneauId)!
        .sommets.find((x) => x.id === proches[0]!.sommetId)!;
      point = { lon: s.lon, lat: s.lat };
      accroche = `cible accrochée sur un sommet de ${proches[0].figureId}`;
    }
    setCible(point);
    setCibleAccrochee(accroche);
    setPreparation(preparerDeplacementPartage(et.ch, d, point, refTolerance.current));
  }, []);

  /* ── Le clic, pendant le chantier : désigner un sommet, puis viser ─────── */
  useEffect(() => {
    const c = carteGL;
    if (!c || !chantier) return;
    const surClic = (e: MapMouseEvent) => {
      const px = 8;
      const sommets = c.queryRenderedFeatures(
        [[e.point.x - px, e.point.y - px], [e.point.x + px, e.point.y + px]],
        { layers: ['chantier-sommets', 'chantier-designe'] });
      if (sommets[0]) {
        const p = sommets[0].properties as Record<string, string>;
        const d: Designation = { figureId: p.fig!, anneauId: p.anneau!, sommetId: p.sommet! };
        setDesigne(d);
        setPreparation(null); setCible(null); setCibleAccrochee(null);
        const et = refChantier.current!;
        const f = et.ch.figures.get(d.figureId)!;
        const s = f.anneaux.find((a) => a.id === d.anneauId)!.sommets.find((x) => x.id === d.sommetId)!;
        setCoincidents(sommetsCoincidents(et.ch, s, refTolerance.current));
        return;
      }
      if (refDesigne.current) preparerVers({ lon: e.lngLat.lng, lat: e.lngLat.lat });
    };
    c.on('click', surClic);
    return () => { c.off('click', surClic); };
  }, [carteGL, chantier, preparerVers]);

  /** La cible au clavier — D.946 : distance + gisement depuis le sommet désigné. */
  const viserAuCogo = useCallback(() => {
    const et = refChantier.current, d = refDesigne.current;
    if (!et || !d) return;
    const dist = Number(cogoDistance.trim().replace(',', '.'));
    const gis = Number(cogoGisement.trim().replace(',', '.'));
    if (!Number.isFinite(dist) || dist <= 0 || !Number.isFinite(gis)) {
      setErreur('Saisie COGO : distance strictement positive et gisement numériques.'); return;
    }
    const f = et.ch.figures.get(d.figureId)!;
    const s = f.anneaux.find((a) => a.id === d.anneauId)!.sommets.find((x) => x.id === d.sommetId)!;
    setErreur(null);
    preparerVers(avancerAuGisement(s.lon, s.lat, gis, uniteCogo, dist));
  }, [cogoDistance, cogoGisement, uniteCogo, preparerVers]);

  const appliquerPreparation = useCallback(() => {
    const et = refChantier.current, prep = refPreparation.current;
    if (!et || !prep) return;
    executerLot(et.ch, prep.lot);
    setLotsAppliques((n) => n + 1);
    setPreparation(null); setCible(null); setCibleAccrochee(null);
    setDesigne(null); setCoincidents([]);
  }, []);

  /* UNE TOUCHE PAR OUTIL — D.949. Jamais dans un champ de saisie, jamais
     avec un modificateur, et jamais par-dessus un outil déjà ouvert. */
  useEffect(() => {
    const auClavier = (ev: KeyboardEvent) => {
      const cible = ev.target as HTMLElement | null;
      if (cible && /^(INPUT|TEXTAREA|SELECT)$/.test(cible.tagName)) return;
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
      const outil = (TOUCHES_ATELIER as Record<string, string>)[ev.key.toLowerCase()];
      if (!outil) return;
      if (etatTrace.mode !== 'inactif' || refChantier.current) return;
      ev.preventDefault();
      if (outil === 'emprise') gestesTrace.demarrer('surface');
      else if (outil === 'bornes') gestesTrace.demarrer('point');
      else if (outil === 'voie') gestesTrace.demarrer('ligne');
      else if (outil === 'chantier') void ouvrirChantierMitoyen();
    };
    window.addEventListener('keydown', auClavier);
    return () => window.removeEventListener('keydown', auClavier);
  }, [etatTrace.mode, gestesTrace, ouvrirChantierMitoyen]);

  /* ⌘Z / ⇧⌘Z sur les LOTS — jamais sur les commandes : annuler la moitié
     d'un déplacement mitoyen FABRIQUERAIT l'interstice qu'on évite. */
  useEffect(() => {
    if (!chantier) return;
    const auClavier = (e: KeyboardEvent) => {
      const et = refChantier.current;
      if (!et) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) { if (peutRefaireLot(et.ch)) { refaireLot(et.ch); setLotsAppliques((n) => n + 1); } }
        else if (peutAnnulerLot(et.ch)) { annulerLot(et.ch); setLotsAppliques((n) => n + 1); }
      }
      if (e.key === 'Escape') {
        if (refPreparation.current) { setPreparation(null); setCible(null); setCibleAccrochee(null); }
        else if (refDesigne.current) { setDesigne(null); setCoincidents([]); }
        else fermerChantier();
      }
    };
    window.addEventListener('keydown', auClavier);
    return () => window.removeEventListener('keydown', auClavier);
  }, [chantier, fermerChantier]);

  /** Déposer les figures modifiées — lot solidaire dès deux corrections. */
  const deposerChantier = useCallback(async () => {
    const et = refChantier.current;
    if (!et || figuresModifiees.length === 0 || depotEnCours) return;
    if (motif.trim().length < 3) { setErreur('Le motif est obligatoire (3 caractères au moins).'); return; }
    setDepotEnCours(true); setErreur(null);
    try {
      if (!peutAppliquer && peutProposer) {
        /* ⚠️ LE MÉTIER DU GÉOMÈTRE : son chantier devient un PROJET — un lot
           de levés solidaire (2-20) ou un levé seul — jamais une écriture au
           registre. Le plan ne bouge pas, donc on ne le relit pas : le relire
           ferait croire qu'une emprise a changé. */
        if (figuresModifiees.length >= 2) {
          const r = await api.releves.deposerLot({
            corrections: figuresModifiees.map((id) => ({
              identifiantParcelle: id, geometrie: versGeoJSON(et.ch.figures.get(id)!) })),
            methode: refLeve.current.methode,
            precisionAnnonceeM: refLeve.current.precision,
            motif: motif.trim(),
            toleranceM: refTolerance.current,
          });
          setResultatDepot({ projet: r.lot, corrigees: r.membres.length, ecart: null, chevauchements: 0 });
        } else {
          const id = figuresModifiees[0]!;
          const r = await api.releves.deposer({
            identifiantParcelle: id,
            methode: refLeve.current.methode,
            precisionAnnonceeM: refLeve.current.precision,
            geometrie: versGeoJSON(et.ch.figures.get(id)!),
            observations: motif.trim(),
          });
          setResultatDepot({ projet: r.releve.reference, corrigees: 1, ecart: null, chevauchements: 0 });
        }
        setChantier(null); setDesigne(null); setPreparation(null); setCible(null);
        return;
      }
      if (figuresModifiees.length >= 2) {
        const corrections = figuresModifiees.map((id) => ({
          identifiant: id,
          geometrie: versGeoJSON(et.ch.figures.get(id)!),
          origine,
        }));
        const r = await api.parcelles.modifierEmprises({ corrections, motif: motif.trim() });
        setResultatDepot({
          corrigees: r.corrigees.length,
          ecart: r.ecart_max_au_contact_m,
          chevauchements: r.chevauchements,
        });
      } else {
        /* Un déplacement qui n'a finalement touché qu'UN fonds (sommet non
           partagé) rebascule sur la route unitaire — le lot la refuse, à
           raison : un seul chemin d'écriture par acte. */
        const id = figuresModifiees[0]!;
        await api.parcelles.modifierEmprise(id, {
          geometrie: versGeoJSON(et.ch.figures.get(id)!), origine, motif: motif.trim(),
        });
        /* L'écart au contact d'un lot d'une seule figure : mesuré localement,
           contre la voisine la plus proche du chantier — à défaut, dit absent. */
        const autres = [...et.ch.figures.keys()].filter((x) => x !== id);
        const ecarts = autres
          .map((x) => ecartAuContact(et.ch, id, x, refTolerance.current))
          .filter((x): x is number => x !== null);
        setResultatDepot({
          corrigees: 1,
          ecart: ecarts.length > 0 ? Math.max(...ecarts) : null,
          chevauchements: 0,
        });
      }
      await relireVersionDuPlan();
      setChantier(null); setDesigne(null); setPreparation(null); setCible(null);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String(e));
    } finally {
      setDepotEnCours(false);
    }
  }, [figuresModifiees, depotEnCours, motif, origine, relireVersionDuPlan, peutAppliquer, peutProposer]);

  /* ── La barre d'état — mêmes valeurs, même code que la consultation ────── */
  const trace = valeursTraceBarre(etatTrace);
  const sel = valeursSelectionBarre(selectionCourante, null);
  const outilBarre = chantier
    ? `chantier mitoyen — ${chantier.ch.figures.size} fonds`
    : trace.outil;
  const accrocheBarre = chantier
    ? (cibleAccrochee ?? (designe ? `sommet désigné — ${coincidents.length} coïncident(s)` : 'aucun sommet désigné'))
    : trace.accroche;

  const peutDeposer = figuresModifiees.length > 0 && !depotEnCours;

  return (
    <div className="atelier-sig">
      {/* ── La barre d'outils, DOCKÉE — zone `barre`, jamais flottante ───── */}
      <div className="atelier-barre">
        <BarreTrace etat={etatTrace} gestes={gestesTrace} gesteReprise={gesteReprise}
          parcelleDesignee={chantier ? null : parcelleDesignee}
          onModifier={() => { void modifierEmprise(); }} />
        {etatTrace.mode === 'inactif' && !chantier && (
          <button type="button" className="bt-outil"
            disabled={!parcelleDesignee || chargement}
            onClick={() => { void ouvrirChantierMitoyen(); }}
            title={parcelleDesignee
              ? `Ouvrir ${parcelleDesignee} et ses voisines pour un déplacement mitoyen`
              : 'Cliquez d’abord une parcelle sur le plan'}>
            <span className="bt-icone" aria-hidden="true">⧉</span>
            <span>{chargement ? 'Chargement…' : <>Chantier mitoyen <kbd>C</kbd></>}</span>
          </button>
        )}
      </div>

      <Carte
        onSelection={recevoirSelection}
        suspendu={etatTrace.mode !== 'inactif' || chantier !== null}
        seuils={reglages?.zoomMinimal}
        zoomNomsDeRue={reglages?.zoomNomsDeRue}
        vueConseillee={reglages?.vueConseillee}
        couverture={reglages?.couverture}
        onCarte={setCarteGL}
        gabaritTuiles={gabaritTuiles}
      />

      {/* ── Le panneau de l'atelier — zone `etabli`, dockée ──────────────── */}
      <aside className="atelier-etabli">
        {erreur && <p className="erreur" role="alert">{erreur}</p>}

        {resultatDepot && (
          <div className="atelier-bloc atelier-depose">
            <h3>Dépôt inscrit</h3>
            {resultatDepot.projet ? (
              <p>Projet <strong>{resultatDepot.projet}</strong> déposé —{' '}
                {resultatDepot.corrigees} fonds. Le registre n'a <strong>pas</strong> été
                modifié : un agent de l'État décide le lot entier.</p>
            ) : (
            <p>{resultatDepot.corrigees} emprise(s) corrigée(s) en une transaction.</p>
            )}
            {/* ⚠️ `null` N'EST PAS ZÉRO. « Écart nul » et « les fonds ne se
                touchent pas » se ressemblent, et une seule est rassurante. */}
            <p>
              {resultatDepot.projet ? null : resultatDepot.ecart === null
                ? 'Les fonds du lot ne se touchent pas — aucun contact à mesurer.'
                : `Écart maximal au contact : ${resultatDepot.ecart.toFixed(3)} m.`}
              {' '}{resultatDepot.chevauchements === 0
                ? 'Aucun chevauchement.'
                : `${resultatDepot.chevauchements} chevauchement(s) !`}
            </p>
          </div>
        )}

        {!chantier && !resultatDepot && (
          <div className="atelier-bloc">
            <h3>L’atelier</h3>
            <p className="mention">
              Tracer une emprise, corriger un contour, ou ouvrir un
              <strong> chantier mitoyen</strong> : déplacer une borne partagée
              déplace TOUS les fonds qui la portent — le sol reste continu.
            </p>
            <p className="mention">
              {parcelleDesignee
                ? <>Parcelle désignée : <strong>{parcelleDesignee}</strong>.</>
                : 'Cliquez une parcelle sur le plan pour la désigner.'}
            </p>
            {parcelleDesignee && onOuvrirParcelle && (
              <button type="button" className="lien"
                onClick={() => onOuvrirParcelle(parcelleDesignee)}>
                Ouvrir la fiche de {parcelleDesignee}
              </button>
            )}
          </div>
        )}

        {chantier && (
          <>
            <div className="atelier-bloc">
              <h3>Chantier mitoyen</h3>
              <p className="mention">
                {chantier.ch.figures.size} fonds chargés depuis le registre
                {chantier.voisinesProjetEcartees > 0 &&
                  <> · {chantier.voisinesProjetEcartees.toLocaleString('fr-FR')} maille(s) « projet » écartée(s)</>}
              </p>
              {chantier.ecartees.length > 0 && (
                <ul className="atelier-ecartees">
                  {chantier.ecartees.map((x) => (
                    <li key={x.identifiant}><strong>{x.identifiant}</strong> — {x.raison}</li>
                  ))}
                </ul>
              )}
              <ul className="atelier-fonds">
                {[...chantier.ch.figures.keys()].map((id) => (
                  <li key={id} className={figuresModifiees.includes(id) ? 'modifie' : undefined}>
                    <strong>{id}</strong>
                    <span className="mention"> {chantier.fiches.get(id)?.statut ?? ''}</span>
                    {figuresModifiees.includes(id) && <em> — modifiée</em>}
                  </li>
                ))}
              </ul>
              <label className="champ-label" htmlFor="atelier-tolerance">Tolérance de coïncidence (m)</label>
              <input id="atelier-tolerance" type="number" step="0.01" min="0.01" max="1"
                value={toleranceM}
                onChange={(e) => setToleranceM(Math.max(0.01, Math.min(1, Number(e.target.value) || 0.1)))} />
            </div>

            <div className="atelier-bloc">
              <h3>Déplacer un sommet</h3>
              {!designe ? (
                <p className="mention">Cliquez un sommet sur le plan — il sera déplacé
                  avec TOUS ceux qui occupent le même point.</p>
              ) : (
                <>
                  <p>
                    Sommet désigné sur <strong>{designe.figureId}</strong>
                    {' · '}{coincidents.length} sommet(s) coïncident(s) sur{' '}
                    {new Set(coincidents.map((d) => d.figureId)).size} fonds.
                  </p>
                  <p className="mention">Cliquez la destination sur le plan, ou saisissez-la :</p>
                  {/* ⚠️ D.946 — LA SAISIE AU CLAVIER. Distance + gisement depuis
                      le sommet désigné ; le gisement se compte depuis le NORD,
                      sens horaire — jamais l'angle mathématique. */}
                  <div className="atelier-cogo">
                    <label htmlFor="cogo-dist">Distance (m)</label>
                    <input id="cogo-dist" inputMode="decimal" value={cogoDistance}
                      placeholder="ex. 12,40"
                      onChange={(e) => setCogoDistance(e.target.value)} />
                    <label htmlFor="cogo-gis">Gisement</label>
                    <input id="cogo-gis" inputMode="decimal" value={cogoGisement}
                      placeholder={uniteCogo === 'grade' ? 'ex. 112,45' : 'ex. 101,2'}
                      onChange={(e) => setCogoGisement(e.target.value)} />
                    <select aria-label="Unité d'angle" value={uniteCogo}
                      onChange={(e) => setUniteCogo(e.target.value as UniteAngle)}>
                      <option value="grade">gon</option>
                      <option value="degre">degrés</option>
                    </select>
                    <button type="button" className="bouton" onClick={viserAuCogo}>Viser</button>
                  </div>
                </>
              )}

              {preparation && (
                <div className="atelier-preparation">
                  {/* ⚠️ MONTRER AVANT DE BOUGER. Un geste qui s'exécute puis
                      s'explique arrive trop tard. */}
                  <p>
                    <strong>{preparation.lot.libelle}</strong> —{' '}
                    {preparation.deplaces.length} sommet(s) sur{' '}
                    {new Set(preparation.deplaces.map((d) => d.figureId)).size} fonds.
                  </p>
                  {cibleAccrochee && <p className="mention">{cibleAccrochee}.</p>}
                  {preparation.risquesInterstice.length > 0 && (
                    <p className="atelier-risque" role="alert">
                      ⚠️ {preparation.risquesInterstice.length} sommet(s) resteraient EN ARRIÈRE
                      sur un côté déplacé — un biseau s'ouvrirait :{' '}
                      {[...new Set(preparation.risquesInterstice.map((d) => d.figureId))].join(', ')}.
                      L'atelier ne les déplace pas au prorata : ce serait inventer une
                      position que ni le titre ni le levé ne portent.
                    </p>
                  )}
                  <div className="actions">
                    <button type="button" className="bouton" onClick={appliquerPreparation}>
                      Appliquer le déplacement
                    </button>
                    <button type="button" className="lien"
                      onClick={() => { setPreparation(null); setCible(null); setCibleAccrochee(null); }}>
                      Abandonner
                    </button>
                  </div>
                </div>
              )}

              <p className="mention">
                Lots appliqués : {chantier.ch.passe.length} ·{' '}
                <kbd>⌘Z</kbd> annule un LOT entier — jamais la moitié d'un geste mitoyen.
              </p>
            </div>

            <div className="atelier-bloc">
              <h3>Déposer</h3>
              {figuresModifiees.length === 0
                ? <p className="mention">Rien à déposer : aucun fonds modifié.</p>
                : <p>{figuresModifiees.length} fonds modifié(s) : {figuresModifiees.join(', ')}.</p>}
              {!peutAppliquer && peutProposer && (
                <>
                  <p className="mention">
                    Votre dépôt est un <strong>projet de levé</strong> : le registre ne
                    bouge pas — un agent de l'État le contrôle puis l'applique.
                  </p>
                  <label className="champ-label" htmlFor="atelier-methode">Méthode du levé</label>
                  <select id="atelier-methode" value={methodeLeve}
                    onChange={(e) => setMethodeLeve(e.target.value)}>
                    <option value="gnss_rtk">GNSS RTK</option>
                    <option value="gnss_statique">GNSS statique</option>
                    <option value="station_totale">Station totale</option>
                    <option value="gps_navigation">GPS de navigation</option>
                    <option value="photo_interpretation">Photo-interprétation</option>
                  </select>
                  <label className="champ-label" htmlFor="atelier-precision">Précision annoncée (m)</label>
                  <input id="atelier-precision" type="number" min={0.01} max={1000} step={0.01}
                    value={precisionLeve}
                    onChange={(e) => setPrecisionLeve(Number(e.target.value))} />
                </>
              )}
              {peutAppliquer && (<>
              <label className="champ-label" htmlFor="atelier-origine">Origine</label>
              <select id="atelier-origine" value={origine}
                onChange={(e) => setOrigine(e.target.value as DemandeImmatriculation['origine'])}>
                <option value="leve_terrain">Levé de terrain</option>
                <option value="numerisation_plan">Numérisation d’un plan</option>
                <option value="declaration">Déclaration</option>
                <option value="import">Import</option>
              </select>
              </>)}
              <label className="champ-label" htmlFor="atelier-motif">Motif</label>
              <textarea id="atelier-motif" value={motif} rows={2}
                placeholder="ex. bornage contradictoire du 11/10/2026, PV n° …"
                onChange={(e) => setMotif(e.target.value)} />
              <div className="actions">
                <button type="button" className="bouton" disabled={!peutDeposer}
                  onClick={() => { void deposerChantier(); }}>
                  {depotEnCours ? 'Dépôt…'
                    : peutAppliquer
                      ? (figuresModifiees.length >= 2
                          ? `Appliquer ${figuresModifiees.length} corrections au registre (lot solidaire)`
                          : 'Appliquer la correction au registre')
                      : (figuresModifiees.length >= 2
                          ? `Déposer un projet de levé — ${figuresModifiees.length} fonds solidaires`
                          : 'Déposer un projet de levé')}
                </button>
                <button type="button" className="lien" onClick={fermerChantier}>
                  Fermer le chantier <kbd>Échap</kbd>
                </button>
              </div>
            </div>
          </>
        )}
      </aside>

      <ReperesCarte
        carte={carteGL}
        outil={outilBarre}
        accroche={accrocheBarre}
        mesure={trace.mesure}
        selection={chantier && designe ? `sommet de ${designe.figureId}` : sel.texte}
        opposabilite={sel.opposabilite}
        seuils={reglages?.zoomMinimal}
        zoomNomsDeRue={reglages?.zoomNomsDeRue}
        zoomBatiComplet={reglages?.zoomBatiComplet}
      />
    </div>
  );
}
